import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import Nav from "@/components/site/Nav";
import { eur } from "@/lib/shop";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Admin() {
  const [key, setKey] = useState(() => sessionStorage.getItem("admin_key") || "");
  const [authed, setAuthed] = useState(false);
  const [orders, setOrders] = useState([]);
  const [queue, setQueue] = useState([]);
  const [editions, setEditions] = useState([]);
  const [editKey, setEditKey] = useState("");
  const [editSold, setEditSold] = useState("");
  const [error, setError] = useState("");

  const headers = { "x-admin-key": key };

  const load = async (k = key) => {
    try {
      const [o, q, e] = await Promise.all([
        axios.get(`${API}/admin/orders`, { headers: { "x-admin-key": k } }),
        axios.get(`${API}/admin/mto`, { headers: { "x-admin-key": k } }),
        axios.get(`${API}/admin/editions`, { headers: { "x-admin-key": k } }),
      ]);
      setOrders(o.data);
      setQueue(q.data);
      setEditions(e.data.counters);
      setAuthed(true);
      setError("");
    } catch {
      setError("Invalid passcode.");
      setAuthed(false);
    }
  };

  useEffect(() => {
    if (key) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = (e) => {
    e.preventDefault();
    sessionStorage.setItem("admin_key", key);
    load();
  };

  const review = async (sessionId, idx, status) => {
    await axios.post(`${API}/admin/mto/${sessionId}/${idx}`, { status }, { headers });
    load();
  };

  const setFulfilment = async (sessionId, status) => {
    await axios.post(`${API}/admin/orders/${sessionId}/fulfilment`, { status }, { headers });
    load();
  };

  const saveEdition = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/editions`, { key: editKey, sold: parseInt(editSold, 10) }, { headers });
      setEditKey("");
      setEditSold("");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid edition key");
    }
  };

  const exportCsv = async () => {
    const res = await axios.get(`${API}/admin/orders.csv`, { headers, responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = "orders.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="bg-[#faf8f5] min-h-screen">
      <Nav solid />
      <main className="pt-28 md:pt-36 pb-24">
        <div className="mx-auto max-w-[1100px] px-6 md:px-12">
          <h1 className="font-display text-3xl md:text-4xl tracking-tight text-[#111111]">Admin</h1>

          {!authed ? (
            <form onSubmit={login} className="mt-10 max-w-sm" data-testid="admin-login">
              <label className="text-xs uppercase tracking-[0.2em] text-[#111111]/50">Passcode</label>
              <input
                type="password"
                value={key}
                onChange={(e) => setKey(e.target.value)}
                data-testid="admin-passcode"
                className="mt-2 w-full border border-[#111111]/25 bg-white px-4 py-3 text-sm font-light outline-none focus:border-[#111111]"
              />
              {error && <p className="mt-2 text-sm text-[#c2402a]" data-testid="admin-error">{error}</p>}
              <button type="submit" data-testid="admin-login-submit"
                className="mt-4 bg-[#111111] px-7 py-3.5 text-sm font-light text-[#faf8f5] hover:opacity-85 transition-opacity">
                Enter
              </button>
            </form>
          ) : (
            <>
              {/* MTO review queue */}
              <section className="mt-12" data-testid="admin-mto">
                <h2 className="text-xs uppercase tracking-[0.25em] text-[#a89f8e]">
                  Made to Order — review queue ({queue.filter((q) => q.review_status === "pending").length} pending)
                </h2>
                <div className="mt-4 border gallery-line bg-white">
                  {queue.length === 0 && (
                    <p className="px-6 py-6 text-sm font-light text-[#111111]/50">No personalised orders yet.</p>
                  )}
                  {queue.map((q) => (
                    <div key={`${q.session_id}-${q.item_index}`}
                      data-testid={`mto-row-${q.session_id}-${q.item_index}`}
                      className="flex flex-wrap items-center gap-4 border-b border-[#111111]/8 px-6 py-4 last:border-b-0">
                      <span className="font-display italic text-lg text-[#111111]" style={{ fontFamily: "'Caveat', cursive" }}>
                        “{q.word}”
                      </span>
                      <span className="text-xs font-light text-[#111111]/60">
                        {q.size} · {q.color} · {q.framing}
                      </span>
                      <span className={`ml-auto text-[11px] uppercase tracking-[0.15em] ${
                        q.review_status === "approved" ? "text-green-700" :
                        q.review_status === "rejected" ? "text-[#c2402a]" : "text-[#a89f8e]"
                      }`}>
                        {q.review_status}
                      </span>
                      {q.review_status === "pending" && (
                        <div className="flex gap-2">
                          <button onClick={() => review(q.session_id, q.item_index, "approved")}
                            data-testid={`mto-approve-${q.item_index}`}
                            className="border border-[#111111] px-4 py-1.5 text-xs font-light hover:bg-[#111111] hover:text-[#faf8f5] transition-colors">
                            Approve
                          </button>
                          <button onClick={() => review(q.session_id, q.item_index, "rejected")}
                            data-testid={`mto-reject-${q.item_index}`}
                            className="border border-[#c2402a]/50 px-4 py-1.5 text-xs font-light text-[#c2402a] hover:bg-[#c2402a] hover:text-white transition-colors">
                            Reject
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </section>

              {/* Orders + fulfilment (print batch queue) */}
              <section className="mt-14" data-testid="admin-orders">
                <div className="flex items-center justify-between">
                  <h2 className="text-xs uppercase tracking-[0.25em] text-[#a89f8e]">
                    Orders ({orders.length})
                  </h2>
                  <button onClick={exportCsv} data-testid="admin-export"
                    className="link-underline text-xs uppercase tracking-[0.15em] text-[#111111]">
                    Export CSV
                  </button>
                </div>
                <div className="mt-4 border gallery-line bg-white overflow-x-auto">
                  {orders.length === 0 && (
                    <p className="px-6 py-6 text-sm font-light text-[#111111]/50">No paid orders yet.</p>
                  )}
                  {orders.map((o) => (
                    <div key={o.session_id} className="border-b border-[#111111]/8 px-6 py-4 last:border-b-0">
                      <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
                        <span className="font-light text-sm text-[#111111]">{eur(o.amount)}</span>
                        <span className="text-xs font-light text-[#111111]/50">{o.ship_zone} · {new Date(o.created_at).toLocaleDateString()}</span>
                        {o.customer_email && <span className="text-xs font-light text-[#111111]/50">{o.customer_email}</span>}
                        {/* fulfilment pipeline */}
                        <div className="ml-auto flex gap-1.5" data-testid={`fulfilment-${o.session_id}`}>
                          {["pending", "printing", "numbered", "dispatched"].map((s) => (
                            <button
                              key={s}
                              onClick={() => setFulfilment(o.session_id, s)}
                              data-testid={`fulfil-${o.session_id}-${s}`}
                              className={`border px-3 py-1 text-[10px] uppercase tracking-[0.12em] transition-colors ${
                                (o.fulfilment_status || "pending") === s
                                  ? "border-[#111111] bg-[#111111] text-[#faf8f5]"
                                  : "border-[#111111]/20 text-[#111111]/50 hover:border-[#111111]"
                              }`}
                            >
                              {s}
                            </button>
                          ))}
                        </div>
                      </div>
                      <p className="mt-1.5 text-xs font-light text-[#111111]/60">
                        {o.items?.map((i) =>
                          `${i.product_name} — ${i.color} ${i.size}${i.framing === "framed" ? " framed" : ""}${i.tier === "collector" ? " collector" : ""} ×${i.quantity}`
                        ).join(" · ")}
                      </p>
                    </div>
                  ))}
                </div>
              </section>

              {/* Edition counters */}
              <section className="mt-14" data-testid="admin-editions">
                <h2 className="text-xs uppercase tracking-[0.25em] text-[#a89f8e]">
                  Edition counters ({editions.length} variants)
                </h2>
                <form onSubmit={saveEdition} className="mt-4 flex flex-wrap items-center gap-3" data-testid="edition-form">
                  <input
                    value={editKey}
                    onChange={(e) => setEditKey(e.target.value)}
                    placeholder="basquiat:50x70:noir"
                    data-testid="edition-key"
                    className="border border-[#111111]/25 bg-white px-4 py-2.5 text-sm font-light outline-none focus:border-[#111111] w-64"
                  />
                  <input
                    value={editSold}
                    onChange={(e) => setEditSold(e.target.value)}
                    placeholder="sold"
                    type="number"
                    min="0"
                    data-testid="edition-sold"
                    className="border border-[#111111]/25 bg-white px-4 py-2.5 text-sm font-light outline-none focus:border-[#111111] w-24"
                  />
                  <button type="submit" data-testid="edition-save"
                    className="bg-[#111111] px-5 py-2.5 text-xs font-light text-[#faf8f5] hover:opacity-85 transition-opacity">
                    Set counter
                  </button>
                </form>
                <div className="mt-4 border gallery-line bg-white max-h-72 overflow-y-auto">
                  {editions
                    .slice()
                    .sort((a, b) => a.key.localeCompare(b.key))
                    .map((c) => (
                      <div key={c.key} data-testid={`edition-row-${c.key}`}
                        className="flex items-center gap-4 border-b border-[#111111]/8 px-6 py-2.5 last:border-b-0">
                        <span className="font-light text-sm text-[#111111] flex-1">{c.key}</span>
                        <span className="text-xs font-light text-[#111111]/60">
                          {c.sold} sold / {c.total} — {c.total - c.sold} left
                        </span>
                        {c.total - c.sold === 0 && (
                          <span className="text-[10px] uppercase tracking-[0.15em] text-[#c2402a]">Sold out</span>
                        )}
                      </div>
                    ))}
                </div>
              </section>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
